import { useState } from 'react';
import { PencilIcon, TrashIcon, ShareIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import type { Photo } from '../../api/photos';
import { getPhotoUrl, getPhotoUrlFromObject } from '../../api/photos';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../ui/BrandModal';

interface PhotoGalleryProps {
  photos: Photo[];
  onPhotoDelete?: (photoId: string) => Promise<void> | void;
  onCaptionUpdate?: (photoId: string, caption: string) => Promise<void> | void;
  onAvatarSelect?: (photoId: string) => Promise<void> | void;
  selectedAvatar?: string;
}

export default function PhotoGallery({
  photos,
  onPhotoDelete,
  onCaptionUpdate,
  onAvatarSelect,
  selectedAvatar
}: PhotoGalleryProps) {
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [editingCaption, setEditingCaption] = useState(false);
  const [caption, setCaption] = useState('');
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handlePhotoClick = (photo: Photo) => {
    setSelectedPhoto(photo);
    setCaption(photo.caption || '');
  };

  const handleClose = () => {
    setSelectedPhoto(null);
    setEditingCaption(false);
  };

  const handleCaptionSave = async () => {
    if (selectedPhoto) {
      try {
        await onCaptionUpdate?.(selectedPhoto.id, caption);
        setEditingCaption(false);
        toast.success('Caption updated successfully');
      } catch (error) {
        console.error('Failed to update caption:', error);
        toast.error('Failed to update caption');
      }
    }
  };

  const handleDelete = async (photoId: string) => {
    if (!confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    try {
      setDeleting(true);
      await onPhotoDelete?.(photoId);
      if (selectedPhoto?.id === photoId) {
        setSelectedPhoto(null);
      }
      toast.success('Photo deleted successfully');
    } catch (error) {
      console.error('Failed to delete photo:', error);
      toast.error('Failed to delete photo');
    } finally {
      setDeleting(false);
    }
  };

  const handleShare = async (photo: Photo) => {
    const photoUrl = getPhotoUrl(photo.url);
    const shareData = {
      title: photo.caption || 'Bird Photo',
      text: `Check out this bird photo${photo.caption ? `: ${photo.caption}` : ''}`,
      url: photoUrl
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        setShowShareMenu(true);
      }
    } catch (error) {
      console.error('Error sharing:', error);
      setShowShareMenu(true);
    }
  };

  const shareViaEmail = (photo: Photo) => {
    const photoUrl = getPhotoUrl(photo.url);
    const subject = encodeURIComponent(photo.caption || 'Bird Photo');
    const body = encodeURIComponent(`Check out this bird photo: ${photoUrl}`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
    setShowShareMenu(false);
  };

  const shareViaWhatsApp = (photo: Photo) => {
    const photoUrl = getPhotoUrl(photo.url);
    const text = encodeURIComponent(`${photo.caption || 'Check out this bird photo'}: ${photoUrl}`);
    window.open(`https://wa.me/?text=${text}`);
    setShowShareMenu(false);
  };

  const shareViaTelegram = (photo: Photo) => {
    const photoUrl = getPhotoUrl(photo.url);
    const text = encodeURIComponent(`${photo.caption || 'Check out this bird photo'}: ${photoUrl}`);
    window.open(`https://t.me/share/url?url=${encodeURIComponent(photoUrl)}&text=${text}`);
    setShowShareMenu(false);
  };

  const copyLink = async (photo: Photo) => {
    const photoUrl = getPhotoUrl(photo.url);
    try {
      await navigator.clipboard.writeText(photoUrl);
      alert('Link copied to clipboard!');
      setShowShareMenu(false);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  return (
    <div className="space-y-4">
      {/* Photo Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {photos.map((photo) => (
          <div
            key={photo.id}
            className="relative group aspect-square cursor-pointer overflow-hidden rounded-lg"
            onClick={() => handlePhotoClick(photo)}
          >
            <img
              src={getPhotoUrlFromObject(photo)}
              alt={photo.caption || 'Bird photo'}
              className="object-cover w-full h-full transition-transform group-hover:scale-105"
            />
            {/* Avatar indicator */}
            {selectedAvatar === photo.id && (
              <div className="absolute top-2 right-2 bg-primary-500 text-white px-2 py-1 rounded-md text-xs">
                Avatar
              </div>
            )}
            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <div className="text-white text-sm truncate px-2">
                {photo.caption || 'Click to view'}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal View */}
      <BrandModal open={!!selectedPhoto} onClose={handleClose} maxWidthClass="max-w-4xl">
        {selectedPhoto ? (
          <>
            <BrandModalHeader title={selectedPhoto.caption || 'Photo'} onClose={handleClose} />
            <BrandModalBody>
              <div className="aspect-video relative">
                <img
                  src={getPhotoUrlFromObject(selectedPhoto)}
                  alt={selectedPhoto.caption || 'Bird photo'}
                  className="object-contain w-full h-full"
                />
              </div>

              <div className="mt-4 space-y-4">
                {editingCaption ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={caption}
                      onChange={(e) => setCaption(e.target.value)}
                      className="input flex-1"
                      placeholder="Enter caption..."
                    />
                    <button
                      type="button"
                      onClick={handleCaptionSave}
                      className="btn btn-primary text-sm"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingCaption(false)}
                      className="btn btn-secondary text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <p className="text-slate-700">{selectedPhoto.caption || 'No caption'}</p>
                    <div className="flex gap-2">
                      {onAvatarSelect && (
                        <button
                          type="button"
                          onClick={() => onAvatarSelect(selectedPhoto.id)}
                          className={`btn text-sm ${
                            selectedAvatar === selectedPhoto.id
                              ? 'btn-primary'
                              : 'btn-secondary'
                          }`}
                        >
                          {selectedAvatar === selectedPhoto.id ? 'Current Avatar' : 'Set as Avatar'}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setEditingCaption(true)}
                        className="p-2 text-slate-500 hover:text-slate-700"
                        aria-label="Edit caption"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(selectedPhoto.id)}
                        disabled={deleting}
                        className="p-2 text-slate-500 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Delete photo"
                      >
                        {deleting ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-600" />
                        ) : (
                          <TrashIcon className="h-5 w-5" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleShare(selectedPhoto)}
                        className="p-2 text-slate-500 hover:text-slate-700"
                        aria-label="Share photo"
                      >
                        <ShareIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </BrandModalBody>
            <BrandModalFooter>
              <button type="button" onClick={handleClose} className="btn btn-secondary text-sm">
                Close
              </button>
            </BrandModalFooter>
          </>
        ) : null}
      </BrandModal>

      {/* Share Menu Modal */}
      <BrandModal
        open={showShareMenu}
        onClose={() => setShowShareMenu(false)}
        maxWidthClass="max-w-sm"
        zIndexClass="z-[60]"
      >
        <BrandModalHeader title="Share Photo" onClose={() => setShowShareMenu(false)} />
        <BrandModalBody>
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => selectedPhoto && shareViaWhatsApp(selectedPhoto)}
              className="w-full text-left px-4 py-2 hover:bg-slate-100 rounded-md"
            >
              Share via WhatsApp
            </button>
            <button
              type="button"
              onClick={() => selectedPhoto && shareViaTelegram(selectedPhoto)}
              className="w-full text-left px-4 py-2 hover:bg-slate-100 rounded-md"
            >
              Share via Telegram
            </button>
            <button
              type="button"
              onClick={() => selectedPhoto && shareViaEmail(selectedPhoto)}
              className="w-full text-left px-4 py-2 hover:bg-slate-100 rounded-md"
            >
              Share via Email
            </button>
            <button
              type="button"
              onClick={() => selectedPhoto && copyLink(selectedPhoto)}
              className="w-full text-left px-4 py-2 hover:bg-slate-100 rounded-md"
            >
              Copy Link
            </button>
          </div>
        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            className="btn btn-secondary text-sm"
            onClick={() => setShowShareMenu(false)}
          >
            Cancel
          </button>
        </BrandModalFooter>
      </BrandModal>
    </div>
  );
}
